import clsx from 'clsx';
import FullPageModal from 'ui/ds/FullPageModal';
import StatusCard from './StatusCard';
import Divider from 'ui/ds/Divider';
import BottomDrawer from 'ui/retail-ds/BottomDrawer';
import useBreakpoints from '@/hooks/useBreakpoints';
import { Loader } from '../Layout/Loader';
import { getLogoPath } from '@/utils/getLogoMap';
import { EndorsementMemberStatusEnum, TMemberWithDependent } from '@/types';
import { formatDate } from '@/utils/formatDate';
import EmptyRequestHistory from './EmptyRequestHistory';
import { CopyIcon } from 'ui/icons';
import toast from 'react-hot-toast';
import { PLUM__EMAILS } from '@/constants';
import { useAppContext } from '@/context/AppContext';
import { Button } from 'ui/mobile-ds';

interface IProps {
  isOpen: boolean;
  onOpenChange: (open?: boolean) => void;
  requestHistoryData: TMemberWithDependent;
  isRequestHistoryLoading: boolean;
}

const RequestHistory = ({
  isOpen,
  onOpenChange,
  requestHistoryData,
  isRequestHistoryLoading,
}: IProps) => {
  const { isMobile } = useBreakpoints();
  const handleCtaClick = () => {
    onOpenChange(false);
  };
  const { isWebView } = useAppContext();
  const copyToClipboard = async () => {
    await navigator.clipboard.writeText(PLUM__EMAILS.CARE_ID);
    if (isWebView) {
      window?.NativeBridge?.onTextCopy?.(PLUM__EMAILS.CARE_ID);
    } else {
      await navigator.clipboard.writeText(PLUM__EMAILS.CARE_ID);
    }

    toast.success('Email id copied');
  };
  const content = (
    <>
      {isRequestHistoryLoading ? (
        <Loader />
      ) : (
        <div
          className={clsx({
            'bg-white p-16 rounded-3xl overflow-auto h-max': true,
            'w-[55%] flex items-center justify-start m-auto mt-12': !isMobile,
            'p-5 h-full': isMobile,
          })}
        >
          <div className="flex flex-col gap-8 w-full h-full">
            <p className="text-2xl font-chromatica font-bold text-gray-600">
              Request History
            </p>
            {requestHistoryData?.dependents.length === 0 ? (
              <EmptyRequestHistory handleCtaClick={handleCtaClick} />
            ) : (
              <>
                {requestHistoryData?.dependents.map((request: any) => (
                  <div className="flex flex-col items-start p-4 md:px-5 md:py-6 border-[1px] border-secondary-200 rounded-2xl w-full">
                    <div className="flex gap-2 items-start">
                      <img
                        src={getLogoPath(request.relationship, request.gender)}
                        alt={`${request.relationship} ${request.gender}`}
                        height={44}
                        width={44}
                      />
                      <div className="flex flex-col items-start gap-1">
                        <p className="text-gray-800 font-semibold text-lg md:text-xl capitalize">
                          {request.firstName + ' ' + request.lastName}
                        </p>
                        <p className="text-gray-400 text-sm md:text-base font-medium">
                          <span className="capitalize">
                            {request.relationship.toLowerCase()}
                          </span>{' '}
                          &bull; Added on {formatDate(request.createdAt)}
                        </p>
                        <StatusCard status={request.endorsementStatus} />
                      </div>
                    </div>
                    {request.endorsementStatus ===
                      EndorsementMemberStatusEnum.Failed && (
                      <>
                        <div className="mt-4 md:mt-6" />
                        <div className="w-full">
                          <Divider className="border-dashed border-mobile-gray-200" />
                        </div>
                        <div className="mt-4 md:mb-6" />
                        <p className="text-sm md:text-base text-gray-400 flex flex-col md:flex-row gap-1">
                          Please reach our support
                          <p className="text-mobile-hero-100">
                            <Button
                              intent="tertiary"
                              className="min-w-0 h-auto font-normal gap-1"
                              onClick={copyToClipboard}
                            >
                              care@plumhq.com
                              <CopyIcon fill="#EE4F5E" />
                            </Button>
                          </p>
                        </p>
                      </>
                    )}
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
  return (
    <>
      {isMobile ? (
        <BottomDrawer
          mode="full"
          className="max-h-[90vh] rounded-t-3xl p-0 h-max min-h-[100px]"
          onClose={() => {
            onOpenChange(false);
          }}
          open={isOpen}
        >
          {content}
        </BottomDrawer>
      ) : (
        <FullPageModal isOpen={isOpen} onClose={onOpenChange} headerText="">
          {content}
        </FullPageModal>
      )}
    </>
  );
};

export default RequestHistory;
